import axios, { AxiosInstance } from "axios";
import type {
  User,
  CandidateSearchResult,
  ScoreCategory,
  Score,
  ApiResponse,
} from "../types";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:3000/api",
      timeout: 30000,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.removeToken();
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  private getToken(): string | null {
    return localStorage.getItem("auth_token");
  }

  private setToken(token: string): void {
    localStorage.setItem("auth_token", token);
  }

  private removeToken(): void {
    localStorage.removeItem("auth_token");
  }

  // Auth methods
  async verifyToken(token: string): Promise<ApiResponse<{ user: User }>> {
    this.setToken(token);
    const response = await this.api.post("/auth/verify");
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    const response = await this.api.get("/auth/me");
    return response.data;
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    const response = await this.api.post("/auth/logout");
    this.removeToken();
    return response.data;
  }

  // Candidate methods
  async searchCandidate(
    citizenId: string
  ): Promise<ApiResponse<CandidateSearchResult>> {
    const response = await this.api.get(
      `/candidates/mp101/search/${citizenId}`
    );
    return response.data;
  }

  async getCandidateParsed(citizenId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(
      `/candidates/mp101/search/${citizenId}/parsed`
    );
    return response.data;
  }

  async searchPC101(citizenId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(
      `/candidates/pc101/search/${citizenId}`
    );
    return response.data;
  }

  // Score methods
  async getScoreCategories(): Promise<ApiResponse<ScoreCategory[]>> {
    const response = await this.api.get("/scores/categories");
    return response.data;
  }

  async getCandidateScores(candidateId: string): Promise<ApiResponse<Score[]>> {
    const response = await this.api.get(`/scores/${candidateId}`);
    return response.data;
  }

  async submitScores(
    candidateId: string,
    scores: Record<string, number>
  ): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/scores/${candidateId}`, { scores });
    return response.data;
  }

  async updateScores(
    candidateId: string,
    scores: Record<string, number>
  ): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/scores/${candidateId}`, { scores });
    return response.data;
  }

  async deleteScores(candidateId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/scores/${candidateId}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.api.get("/health");
    return response.data;
  }
}

export default new ApiService();
